import { utils, writeFileXLSX } from "xlsx";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

export interface ExportRow {
  _id: string;
  title: string;
  manufacturer: string;
  partNumber?: string;
  model?: string;
  category: string;
  condition: string;
  quantity: number;
  price?: number;
  currency: string;
  description?: string;
  country?: string;
  createdAt?: string;
  updatedAt?: string;
}

export function downloadInventoryCSV(rows: ExportRow[], filename: string) {
  // Define explicit column headers in the desired order (for exports, includes Currency and Country)
  const headers = [
    "Manufacturer",
    "PartNumber",
    "Model",
    "Quantity",
    "Condition",
    "Price",
    "Currency",
    "Category",
    "Description",
    "Country",
  ];

  // Map rows to data arrays matching the header order
  const dataRows = rows.map((row) => [
    row.manufacturer || "",
    row.partNumber || "",
    row.model || "",
    row.quantity ?? 0,
    row.condition || "",
    row.price ?? 0,
    row.currency || "",
    row.category || "",
    row.description || "",
    row.country || "",
  ]);

  // Create worksheet from array of arrays (headers + data rows) and convert to CSV
  const ws = utils.aoa_to_sheet([headers, ...dataRows]);
  const csv = utils.sheet_to_csv(ws);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, filename);
}

/**
 * Download an empty inventory upload template (CSV format)
 * This template does NOT include Currency and Country columns
 */
export function downloadInventoryTemplateCSV(): void {
  const headers = [
    "Manufacturer",
    "PartNumber",
    "Model",
    "Quantity",
    "Condition",
    "Price",
    "Category",
    "Description",
  ];

  // Create worksheet with headers only (no data rows)
  const ws = utils.aoa_to_sheet([headers]);
  const csv = utils.sheet_to_csv(ws);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, "inventory-upload-template.csv");
}

export function downloadInventoryXLSX(rows: ExportRow[], filename: string) {
  // Define explicit column headers in the desired order (for exports, includes Currency and Country)
  const headers = [
    "Manufacturer",
    "PartNumber",
    "Model",
    "Quantity",
    "Condition",
    "Price",
    "Currency",
    "Category",
    "Description",
    "Country",
  ];

  // Map rows to data arrays matching the header order
  const dataRows = rows.map((row) => [
    row.manufacturer || "",
    row.partNumber || "",
    row.model || "",
    row.quantity ?? 0,
    row.condition || "",
    row.price ?? 0,
    row.currency || "",
    row.category || "",
    row.description || "",
    row.country || "",
  ]);

  // Create worksheet from array of arrays (headers + data rows)
  const ws = utils.aoa_to_sheet([headers, ...dataRows]);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, "Inventory");
  writeFileXLSX(wb, filename);
}

/**
 * [2026-02-24] BR-18: Download inventory upload template (XLSX) with data-validation dropdowns
 * Uses exceljs for native Excel data validation support on Condition and Category columns.
 * This template does NOT include Currency and Country columns.
 */
export async function downloadInventoryTemplateXLSX(): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Inventory");

  // Define columns with headers and widths
  sheet.columns = [
    { header: "Manufacturer", key: "manufacturer", width: 18 },
    { header: "PartNumber", key: "partNumber", width: 18 },
    { header: "Model", key: "model", width: 18 },
    { header: "Quantity", key: "quantity", width: 12 },
    { header: "Condition", key: "condition", width: 22 },
    { header: "Price", key: "price", width: 12 },
    { header: "Category", key: "category", width: 30 },
    { header: "Description", key: "description", width: 35 },
  ];

  // Bold header row
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };

  // Sample data row to guide users
  sheet.addRow({
    manufacturer: "DJI",
    partNumber: "M30T-COMBO",
    model: "Matrice 30T",
    quantity: 2,
    condition: "NEW Retail",
    price: 12000,
    category: "Drones/UAV",
    description: "Enterprise thermal drone combo",
  });

  // Dropdown values
  const conditions = [
    "NEW Retail",
    "New Bulk",
    "Refurbished",
    "Used",
    "AS-IS",
  ];

  const categories = [
    "Drones/UAV",
    "Motors & Props",
    "Cameras & Gimbals",
    "Batteries",
    "GCS – Ground Control Systems",
    "Storage",
    "Components",
    "Robotics",
    "Software",
    "C-UAS",
    "Services",
  ];

  // Apply data validation dropdowns on rows 2–1000
  const TEMPLATE_ROWS = 1000;
  for (let row = 2; row <= TEMPLATE_ROWS; row++) {
    // Condition column (E)
    sheet.getCell(`E${row}`).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`"${conditions.join(",")}"`],
      showErrorMessage: true,
      errorTitle: "Invalid Condition",
      error: `Must be one of: ${conditions.join(", ")}`,
    };

    // Category column (G)
    sheet.getCell(`G${row}`).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`"${categories.join(",")}"`],
      showErrorMessage: true,
      errorTitle: "Invalid Category",
      error: "Select a category from the dropdown list.",
    };
  }

  // Write to buffer and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "inventory-upload-template.xlsx");
}

export function mapToExportRows(items: any[]): ExportRow[] {
  return items.map((item) => ({
    _id: item._id || "",
    title: item.title || "",
    manufacturer: item.manufacturer || "",
    partNumber: item.partNumber || "",
    model: item.model || "",
    category: item.category || "",
    condition: item.condition || "",
    quantity: item.quantity || 0,
    price: item.price || item.convertedPrice || 0,
    currency: item.currency || item.displayCurrency || "USD",
    description: item.description || "",
    country: item.country || "",
    createdAt: item.createdAt || "",
    updatedAt: item.updatedAt || "",
  }));
}

export function generateFilename(extension: "csv" | "xlsx"): string {
  // Both CSV and XLSX files are named "inventory" (no timestamp)
  return `inventory.${extension}`;
}
